import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Translate, withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import css from './CreativeTagsTab.css';
import sharedCss from './sharedTabStyles.css';
import { CreativeType } from '../../types';
import { UPDATE_CREATIVE_TAGS } from '../../mutations';
import { TRACK_EVENT_PREFIX, NONE_TAG_VALUE } from '../../consts';
import { Button, Table, TableCell, WarningMessage } from '../../../components/widgets';
import TableCellTags from '../../../components/widgets/TableCellTags';
import { DimensionCategoryToColor, NO_TAGS_COLOR } from '../../dimensionsUtils';
import SearchTextField from '../../../components/molecules/SearchTextField/SearchTextField';
import { IconSizes } from '../../../components/foundations/Icon';
import { GeneralPopup, PopupTypes } from '../../../components/partials';
import Snackbar from '../../../components/widgets/Snackbar/Snackbar';
import { trackMixpanelEvent } from '../../../utils/general';
import { getUserData } from '../../../selectors/user';
import { ROLE_ORG_ADMIN, ROLE_STANDARD } from '../../../teamManagement/utils';
export const SKELETONS = Array(4).fill({});
export const NO_AI_DIMENSIONS = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.NO_AI_DIMENSIONS';
export const NO_AI_TAGS = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.NO_AI_TAGS';
export const NO_FILTERED_AI_TAGS = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.NO_FILTERED_AI_TAGS';
export const SEARCH_TAGS_PLACEHOLDER = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.SEARCH_TAGS_PLACEHOLDER';
export const NO_TAGS_AVAILABLE = 'STATIC.PAGES.CREATIVE_GALLERY.NO_TAGS_AVAILABLE';
export const DIMENSION_NAME_CELL_TEST_ID = 'dimension-name-cell';
const SNACKBAR_AUTO_HIDE_DURATION = 3000;
const DELETE_TAGS_EVENT = 'Delete Tags';
const ADD_TAGS_EVENT = 'Add Tags';

const getMessageContent = (noTagsData, filteredCreativeAITags) =>
    noTagsData ? NO_AI_TAGS : !filteredCreativeAITags.length ? NO_FILTERED_AI_TAGS : '';

const getTagsTableMetadata = (isLoading, translate, editMode, onDeleteTag, onAddTag, appId) => [
    {
        name: 'dimensionName',
        displayName: 'Dimensions',
        cellComponent: TableCell,
        cellProps: {
            className: css.dimensionNameTableCell,
            dataTestId: DIMENSION_NAME_CELL_TEST_ID,
            isLoading,
            loaderClass: css.cellLoader,
        },
    },
    {
        name: 'tags',
        displayName: 'Values',
        cellComponent: TableCellTags,
        cellValues: [
            {
                propName: 'color',
                dataKey: 'category',
                transformation: category => DimensionCategoryToColor[category],
            },
            {
                propName: 'dimensionName',
                dataKey: 'dimensionName',
            },
        ],
        cellProps: {
            isLoading,
            loaderClass: css.cellLoader,
            tagClassName: css.tag,
            defaultTag: translate(NO_TAGS_AVAILABLE),
            defaultColor: NO_TAGS_COLOR,
            isEditMode: editMode,
            onDeleteTag,
            onAddTag,
            appId,
        },
    },
];

const CreativeTagsTab = ({ app, source, imageHash, translate, onHasUnsavedChanges = () => {}, isLoading = false, aiDimensions = [] }) => {
    const userData = useSelector(state => getUserData(state));
    const [search, setSearch] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [changesCount, setChangesCount] = useState(0);
    const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
    const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '', title: '' });

    const deletedTagsRef = useRef(new Map());
    const addedTagsRef = useRef(new Map());
    const canEditTags = userData?.role_type === ROLE_STANDARD || userData?.role_type === ROLE_ORG_ADMIN;

    const getDeleteCount = () => Array.from(deletedTagsRef.current.values()).reduce((sum, set) => sum + set.size, 0);

    const getAddCount = () => Array.from(addedTagsRef.current.values()).reduce((sum, set) => sum + set.size, 0);

    const totalChanges = useMemo(() => getDeleteCount() + getAddCount(), [changesCount]);

    const clearTagRefs = () => {
        deletedTagsRef.current.clear();
        addedTagsRef.current.clear();
        setChangesCount(0);
    };

    useEffect(() => {
        const hasUnsavedChanges = isEditing && totalChanges > 0;
        onHasUnsavedChanges(hasUnsavedChanges);
    }, [isEditing, changesCount]);

    useEffect(() => {
        if (!isLoading && !isEditing && aiDimensions.length) clearTagRefs();

        return () => {
            clearTagRefs();
            onHasUnsavedChanges(false);
        };
    }, [aiDimensions]);

    const showSnackBar = (type, title, message) => {
        setSnackbar({ open: true, type, title, message });
    };

    const hideSnackBar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const computeDisplayedTags = dimension => {
        const deletedSet = deletedTagsRef.current.get(dimension.dimensionName);
        const addedSet = addedTagsRef.current.get(dimension.dimensionName);

        const filteredTags = deletedSet?.size ? dimension.tags.filter(tag => !deletedSet.has(tag)) : dimension.tags;

        return addedSet?.size ? [...filteredTags, ...addedSet] : filteredTags;
    };

    const displayedCreativeAITags = useMemo(() => {
        return aiDimensions.map(aiDimension => ({
            ...aiDimension,
            tags: computeDisplayedTags(aiDimension),
        }));
    }, [aiDimensions, changesCount]);

    const [updateCreativeTagsMutation, { loading: isSaving }] = useMutation(UPDATE_CREATIVE_TAGS, {
        update(cache, { data }) {
            if (data?.updateCreativeTags?.ok) {
                cache.evict({
                    id: 'ROOT_QUERY',
                    fieldName: 'creativeTags',
                });
                cache.gc();
            }
        },
    });

    const noTagsData = !displayedCreativeAITags.some(aiDimension => !!aiDimension.tags.length);

    const filteredCreativeAITags = displayedCreativeAITags
        .map(aiDimension => {
            const searchLower = search.toLowerCase();
            const matchesDimension = aiDimension.dimensionName.toLowerCase().includes(searchLower);
            if (matchesDimension) return aiDimension;

            const filteredTags = aiDimension.tags.filter(tag => tag.toLowerCase().includes(searchLower));
            return filteredTags.length ? { ...aiDimension, tags: filteredTags } : null;
        })
        .filter(Boolean);

    const noTagsDisplayed = !isLoading && (!filteredCreativeAITags.length || noTagsData);

    const removeFromTagSet = (tagsMap, tag, dimensionName) => {
        const tagsSet = tagsMap.get(dimensionName);
        if (!tagsSet?.has(tag)) return false;

        tagsSet.delete(tag);
        if (tagsSet.size === 0) {
            tagsMap.delete(dimensionName);
        }
        return true;
    };

    const addToTagSet = (tagsMap, tag, dimensionName) => {
        const tagsSet = tagsMap.get(dimensionName) || new Set();
        tagsSet.add(tag);
        tagsMap.set(dimensionName, tagsSet);
    };

    const tagExistsInFetchedDimension = (dimensionName, tag) => {
        const fetchedDimension = aiDimensions.find(d => d.dimensionName === dimensionName);
        return fetchedDimension?.tags.includes(tag);
    };

    const onDeleteTag = (dimensionName, tag) => {
        const existsInFetchedTags = tagExistsInFetchedDimension(dimensionName, tag);

        if (!existsInFetchedTags) {
            removeFromTagSet(addedTagsRef.current, tag, dimensionName);
        } else {
            addToTagSet(deletedTagsRef.current, tag, dimensionName);
        }

        setChangesCount(prevCount => prevCount + 1);
    };

    const onAddTag = (dimensionName, tag) => {
        const isRestored = removeFromTagSet(deletedTagsRef.current, tag, dimensionName);

        if (!isRestored) {
            const capitalizedTag = tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
            addToTagSet(addedTagsRef.current, capitalizedTag, dimensionName);
        }

        if (tagExistsInFetchedDimension(dimensionName, NONE_TAG_VALUE)) {
            addToTagSet(deletedTagsRef.current, NONE_TAG_VALUE, dimensionName);
        }

        setChangesCount(prevCount => prevCount + 1);
    };

    const onSaveChanges = useCallback(() => {
        if (totalChanges === 0) {
            setIsEditing(false);
            return;
        }

        setShowSaveConfirmation(true);
    }, [totalChanges]);

    const computeTagPayload = tagsRef => {
        return Array.from(tagsRef.current.entries()).map(([dimensionName, tagsSet]) => ({
            dimensionName,
            tagValues: Array.from(tagsSet),
        }));
    };

    const showSuccessSnackbar = () =>
        showSnackBar(
            'success',
            translate('STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.SUCCESS.TITLE'),
            translate('STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.SUCCESS.MESSAGE')
        );

    const showErrorSnackbar = (isUnexpected = false) =>
        showSnackBar(
            'error',
            translate('STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.ERROR.TITLE'),
            translate(
                `STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.ERROR.${isUnexpected ? 'UNEXPECTED' : 'MESSAGE'}`
            )
        );

    const trackTagsEvent = (eventName, tagsPayload) => {
        if (!tagsPayload) return;

        tagsPayload.forEach(({ dimensionName, tagValues }) => {
            trackMixpanelEvent(TRACK_EVENT_PREFIX, eventName, {
                app,
                source,
                dimensionName,
                tagValues,
            });
        });
    };

    const handleConfirmSave = useCallback(async () => {
        const deleteCount = getDeleteCount();
        const addCount = getAddCount();

        if (deleteCount === 0 && addCount === 0) {
            setIsEditing(false);
            setShowSaveConfirmation(false);
            return;
        }

        const tagsToDelete = deleteCount > 0 ? computeTagPayload(deletedTagsRef) : null;
        const tagsToAdd = addCount > 0 ? computeTagPayload(addedTagsRef) : null;

        trackTagsEvent(DELETE_TAGS_EVENT, tagsToDelete);
        trackTagsEvent(ADD_TAGS_EVENT, tagsToAdd);

        try {
            const result = await updateCreativeTagsMutation({
                variables: {
                    updateCreativeTagsNode: {
                        appId: app,
                        imageHash,
                        tagsToDelete,
                        tagsToAdd,
                    },
                },
            });

            setShowSaveConfirmation(false);

            if (result.data?.updateCreativeTags?.ok) {
                setIsEditing(false);
                showSuccessSnackbar();
            } else {
                showErrorSnackbar();
            }
        } catch {
            setShowSaveConfirmation(false);
            showErrorSnackbar();
        }
    }, [app, source, imageHash, updateCreativeTagsMutation, translate]);

    const resetEditMode = () => {
        clearTagRefs();
        setIsEditing(false);
    };

    const onCancelEdit = useCallback(() => {
        if (totalChanges > 0) {
            setShowDiscardConfirmation(true);
        } else {
            resetEditMode();
        }
    }, [totalChanges]);

    const handleConfirmDiscard = useCallback(() => {
        resetEditMode();
        setShowDiscardConfirmation(false);
    }, []);

    return (
        <div className={sharedCss.tabContainer}>
            <GeneralPopup
                open={showSaveConfirmation}
                type={PopupTypes.INFO_WITH_CANCEL}
                title="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_CONFIRMATION.TITLE"
                text="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_CONFIRMATION.MESSAGE"
                acceptText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_ACTION"
                rejectText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.REJECT"
                onAccept={handleConfirmSave}
                onReject={() => setShowSaveConfirmation(false)}
                showSpinner={isSaving}
            />
            <GeneralPopup
                open={showDiscardConfirmation}
                type={PopupTypes.INFO_WITH_CANCEL}
                title="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.TITLE"
                text="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.MESSAGE"
                acceptText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.ACCEPT"
                rejectText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.REJECT"
                onAccept={handleConfirmDiscard}
                onReject={() => setShowDiscardConfirmation(false)}
            />
            {snackbar.type && (
                <Snackbar
                    title={snackbar.title}
                    type={snackbar.type}
                    message={snackbar.message}
                    open={snackbar.open}
                    autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
                    onClose={hideSnackBar}
                />
            )}
            <div className={classNames(sharedCss.tabTitle, css.tagsTitle)}>
                <Translate id="STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ALL_CREATIVE_TAGS" />
                {canEditTags &&
                    (isEditing ? (
                        <div className={css.tagsActions}>
                            <Button type="flat" level="level2" onClick={onCancelEdit} disabled={isSaving}>
                                <Translate id="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.CANCEL_TAGS" />
                            </Button>
                            <Button
                                type="secondary"
                                level="level2"
                                onClick={onSaveChanges}
                                disabled={isSaving || totalChanges === 0}
                                showSpinner={isSaving}
                            >
                                {totalChanges > 0 ? (
                                    <Translate
                                        id="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_TAGS"
                                        data={{ count: totalChanges }}
                                    />
                                ) : (
                                    <Translate id="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_ACTION" />
                                )}
                            </Button>
                        </div>
                    ) : (
                        <Button
                            type="secondary"
                            level="level2"
                            Icon="edit"
                            onClick={() => setIsEditing(true)}
                            disabled={isSaving}
                        >
                            <Translate id="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.EDIT_TAGS" />
                        </Button>
                    ))}
            </div>
            <SearchTextField
                placeholder={SEARCH_TAGS_PLACEHOLDER}
                clearable
                search={search}
                onSearchChange={setSearch}
                iconSize={IconSizes.X_SMALL}
                disabled={noTagsData || isLoading}
            />
            <div className={css.tagsTableContainer}>
                {noTagsDisplayed && !isEditing ? (
                    <div className={css.messageContainer}>
                        {!aiDimensions.length ? (
                            <WarningMessage
                                showIcon={false}
                                className={css.warningMessage}
                                message={NO_AI_DIMENSIONS}
                            />
                        ) : (
                            <Translate id={getMessageContent(noTagsData, filteredCreativeAITags)} />
                        )}
                    </div>
                ) : (
                    <Table
                        tableClass={css.tagsTable}
                        metadata={getTagsTableMetadata(isLoading, translate, isEditing, onDeleteTag, onAddTag, app)}
                        data={isLoading ? SKELETONS : filteredCreativeAITags}
                        theadClass={css.tagsTableHeader}
                        thClass={css.tagsTableHeaderCell}
                        trClass={css.tagsTableRowCell}
                        zebraTable={false}
                    />
                )}
            </div>
        </div>
    );
};

CreativeTagsTab.propTypes = {
    app: PropTypes.string.isRequired,
    source: PropTypes.string,
    imageHash: CreativeType.imageHash.isRequired,
    translate: PropTypes.func,
    onHasUnsavedChanges: PropTypes.func,
    isLoading: PropTypes.bool,
    aiDimensions: PropTypes.array.isRequired,
};

export default withLocalize(CreativeTagsTab);
